import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Activity, Sparkles, ArrowRight, Camera, Ruler, Dumbbell } from "lucide-react";
import Hero3DBackground from "@/components/apexture/Hero3DBackground";

const HERO_IMG = "https://images.pexels.com/photos/13538710/pexels-photo-13538710.jpeg";
const CLINIC_IMG = "https://images.pexels.com/photos/29807423/pexels-photo-29807423.jpeg";
const REHAB_IMG = "https://images.unsplash.com/photo-1581122584612-713f89daa8eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxyZWhhYiUyMGV4ZXJjaXNlfGVufDB8fHx8MTc4NzY3Mzg4MHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <Hero3DBackground />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7 space-y-8 animate-fade-in">
            <Badge variant="outline" className="border-forest/30 text-forest bg-white px-3 py-1 rounded-full">
              Evidence-based · NASM · Janda
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-forest leading-[1.05] tracking-tight">
              Fix your posture,<br />
              <span className="text-sage italic">without leaving home.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Apexture spots the imbalances behind your neck pain, hunched back, or achy hips —
              then gives you a simple 4-step plan you can actually follow. No jargon, no clinic visits needed.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/safety-screen">
                <Button size="lg" className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7 h-12 gap-2" data-testid="start-assessment-btn">
                  Start Free Check-in <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/posture-pattern">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-forest/30 text-forest hover:bg-forest/5" data-testid="explore-patterns-btn">
                  See the 8 posture types
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sage" /> Safety check first</span>
              <span className="flex items-center gap-2"><Activity className="w-4 h-4 text-sage" /> AI or pick-a-picture</span>
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sage" /> Simple exercise plan</span>
            </div>
          </div>

          {/* Bento image collage */}
          <div className="lg:col-span-5 grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
            <div className="col-span-6 row-span-4 rounded-xl overflow-hidden border border-forest/15">
              <img src={HERO_IMG} alt="Physiotherapist adjusting patient" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-3 row-span-2 rounded-xl overflow-hidden border border-forest/15 bg-forest text-bone p-4 flex flex-col justify-between">
              <span className="text-xs uppercase tracking-[0.2em] opacity-80">Grade Scale</span>
              <div>
                <p className="font-heading text-3xl font-bold">1 · 2 · 3</p>
                <p className="text-xs opacity-80">Mild → Moderate → Severe</p>
              </div>
            </div>
            <div className="col-span-3 row-span-2 rounded-xl overflow-hidden border border-forest/15">
              <img src={REHAB_IMG} alt="Rehab exercise" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-forest/10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, title: "01 · Safety check", body: "Quick questions to make sure you don't need a doctor first." },
            { icon: Camera, title: "02 · Check yourself", body: "Snap photos, use the camera, or just pick the picture that looks most like you." },
            { icon: Ruler, title: "03 · Simple tests", body: "Try a few easy at-home tests (like touching your head to a wall) with clear pictures." },
            { icon: Dumbbell, title: "04 · Your plan", body: "Get a 4-step plan — loosen, stretch, wake up, move well — with videos for every move." },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white border border-forest/10 rounded-xl p-6 hover:border-forest/30 transition-colors">
                <div className="w-10 h-10 rounded-md bg-forest/8 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-forest" />
                </div>
                <p className="font-heading text-lg font-semibold text-forest mb-2">{s.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-forest text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">Built to actually help — not overwhelm you.</h2>
            <p className="opacity-85 leading-relaxed">
              Most posture apps just show you a photo overlay and vague tips. Apexture finds the exact tight
              and weak muscles behind your slump — then hands you a plan you can do at home in 15 minutes a day.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li>• 8 common posture types with clear pictures</li>
              <li>• Chat with our AI physio whenever you're stuck</li>
              <li>• Sign in with Google to save and track progress</li>
            </ul>
          </div>
          <div className="rounded-xl overflow-hidden border border-bone/15">
            <img src={CLINIC_IMG} alt="Clinic setting" className="w-full h-72 object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
